/**
 * Datasets Service
 *
 * Frontend service for the datasets feature. Calls the datasets API endpoints
 * (POST /api/datasets, GET /api/datasets, and GET /api/datasets/metadata) via
 * the API Gateway.
 *
 * The metadata response is cached for the page session since the domains change
 * only on deploy.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Sentinel value for the ``permittedUsers`` list that means "shared with all
 * authenticated users". When present, any authenticated user can download
 * files and add notes — individual sub entries become irrelevant.
 */
export const PERMITTED_USERS_ALL_SENTINEL: string = "__ALL__"

// ---------------------------------------------------------------------------
// Dataset type
// ---------------------------------------------------------------------------

/**
 * A dataset record as returned from the GET /api/datasets endpoint.
 *
 * Mirrors the DynamoDB schema with all stored attributes. File keys are stored
 * per-version in the ``versions`` map rather than at the top level.
 */
export interface Dataset {
  /** Unique identifier (UUID, partition key). */
  datasetId: string
  /** Human-readable dataset title. */
  title: string
  /** Free-text description (optional). */
  description?: string
  /** Dataset topics (user-defined, at least one). */
  dataTopic: string[]
  /** The latest (highest) version number for this dataset. */
  currentVersion: number
  /** Map of version number (as string key) to version metadata including fileKeys. */
  versions: Record<string, DatasetVersion>
  /** Auth0 'sub' claim of the owner. */
  owner: string
  /** ISO timestamp when the dataset was uploaded. */
  uploadDate: string
  /** ISO timestamp of last modification. */
  lastModified: string
  /** Users granted read access beyond the owner (optional). */
  permittedUsers?: string[]
  /** Temporal coverage, e.g. "2000" or "2000-2020" (optional). */
  temporalDimension?: string
  /** Temporal resolutions (optional). */
  temporalResolution?: string[]
  /** Spatial coverage description (optional). */
  spatialDimension?: string
  /** Level of spatial aggregation (optional). */
  spatialAggregationLevel?: string[]
  /** ISO3 country codes (optional). */
  countries?: string[]
  /** Unit(s) of analysis, e.g. Child, Woman, Household, or a custom value (optional). */
  unitOfAnalysis?: string[]
  /** Cross-reference to biblio record (optional). */
  biblioId?: number
  /** Publishability/release status (optional). */
  publishable?: string
  /** S3 file keys for the sharing agreement under the sharing_agreement/ prefix (optional). */
  sharingAgreementFileKeys?: string[]
  /** Free-form text describing usage permissions (optional). */
  sharingAgreementText?: string
  /** Notes attached to the dataset (optional). */
  notes?: DatasetNote[]
}

/**
 * Metadata for a single version of a dataset's files.
 *
 * Each version holds its own set of file keys (which may differ between
 * versions) and an optional deprecation reason explaining why this version
 * was superseded.
 */
export interface DatasetVersion {
  /** S3 object keys (filenames) for this version, stored at {datasetId}/{version}/{filename}. */
  fileKeys: string[]
  /** Human-readable reason this version was deprecated (only present on superseded versions). */
  deprecationReason?: string
}

/** A note attached to a dataset. */
export interface DatasetNote {
  /** The note text content. */
  note: string
  /** Auth0 sub of the user who created the note. */
  createdBy: string
  /** ISO timestamp when the note was created. */
  createdOn: string
}

// ---------------------------------------------------------------------------
// API base URL
// ---------------------------------------------------------------------------

/** Cached API base URL loaded from aws-exports.json. */
let API_BASE_URL: string = ""

/**
 * Load the datasets API base URL from aws-exports.json configuration.
 *
 * Uses VITE_API_URL env var if set, otherwise falls back to the apiUrl from
 * aws-exports.json, and finally to a relative path.
 *
 * @returns The base URL for datasets API endpoints (e.g. "https://host/api/datasets").
 */
async function loadApiBaseUrl(): Promise<string> {
  if (API_BASE_URL) {
    return API_BASE_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = import.meta.env.VITE_API_URL || config.apiUrl || ""
    API_BASE_URL = baseUrl
      ? `${baseUrl}/api/datasets`
      : "/api/datasets"
    return API_BASE_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Datasets API URL not configured")
  }
}

// ---------------------------------------------------------------------------
// Metadata value domains
// ---------------------------------------------------------------------------

/** A country option with display name and stable ISO3 identifier. */
export interface CountryOption {
  /** Human-readable country name for display. */
  countryName: string
  /** ISO 3166-1 alpha-3 code, stored on dataset records. */
  iso3: string
}

/**
 * The allowed values for the constrained dataset metadata fields, used to
 * populate the form's select/checkbox inputs.
 *
 * Fetched from GET /api/datasets/metadata which reads from SSM (static
 * domains) and the MAP admin units API (countries).
 */
export interface DatasetMetadataDomains {
  /** Release/publishability status options. */
  publishable: string[]
  /** Dataset topic seed options (users can also add custom topics). */
  dataTopic: string[]
  /** Temporal resolution options. */
  temporalResolution: string[]
  /** Spatial aggregation level options (a dataset may have one or more). */
  spatialAggregationLevel: string[]
  /** Unit of analysis seed options (users can also specify "Other"). */
  unitOfAnalysis: string[]
  /** Country options fetched from the MAP admin units API. */
  countries: CountryOption[]
}

/** Cached metadata domains — fetched once per page session. */
let _metadataCache: DatasetMetadataDomains | null = null

/**
 * Fetch the dataset metadata value domains from the API.
 *
 * Results are cached in memory for the page session since they only change on
 * deploy. Pass `forceRefresh: true` to bypass the cache if needed.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param forceRefresh - If true, bypasses the in-memory cache.
 * @returns A promise resolving to the metadata domains used to populate the
 *   form's constrained inputs.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getDatasetMetadataDomains(
  accessToken: string,
  forceRefresh: boolean = false
): Promise<DatasetMetadataDomains> {
  if (_metadataCache && !forceRefresh) {
    return _metadataCache
  }

  const baseUrl = await loadApiBaseUrl()
  const url = `${baseUrl}/metadata`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    throw new Error(
      `Failed to fetch dataset metadata: HTTP ${response.status}`
    )
  }

  const body: { metadata: DatasetMetadataDomains } = await response.json()
  _metadataCache = body.metadata
  return _metadataCache
}

// ---------------------------------------------------------------------------
// Permitted users
// ---------------------------------------------------------------------------

/** A user option with display name and stable sub (subject) identifier. */
export interface UserOption {
  /** The Auth0 'sub' claim — a stable UUID for the user. */
  sub: string
  /** Human-readable display name. */
  name: string
  /** Whether the user is still active in Auth0. Missing means active (legacy). */
  is_active?: boolean
}

/** Cached users list — fetched once per page session. */
let _usersCache: UserOption[] | null = null

/**
 * Fetch the list of users that can be granted read access to a dataset.
 *
 * Calls GET /api/users which returns users synced from Auth0. Results are
 * cached in memory for the page session.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param forceRefresh - If true, bypasses the in-memory cache.
 * @param includeInactive - If true, also returns users removed from Auth0.
 * @returns A promise resolving to user options with sub, name, and is_active.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getPermittedUsersOptions(
  accessToken: string,
  forceRefresh: boolean = false,
  includeInactive: boolean = false,
): Promise<UserOption[]> {
  if (_usersCache && !forceRefresh && !includeInactive) {
    return _usersCache
  }

  // Load the base URL from aws-exports.json (same as other endpoints)
  const baseUrl = await loadApiBaseUrl()
  // Users endpoint is at /api/users — strip /api/datasets from baseUrl and add /api/users
  let usersUrl = baseUrl.replace(/\/api\/datasets$/, "/api/users")
  if (includeInactive) {
    usersUrl += "?include_inactive=true"
  }

  const response = await fetch(usersUrl, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch users: HTTP ${response.status}`)
  }

  const body: { users: UserOption[] } = await response.json()

  // Only cache active-only results — mixed results shouldn't be returned for
  // the picker on subsequent calls.
  if (!includeInactive) {
    _usersCache = body.users
  }
  return body.users
}

/**
 * Check whether a dataset is shared with a given user.
 *
 * A dataset is considered shared with a user if the user is the owner, if the
 * ``__ALL__`` sentinel is present in ``permittedUsers``, or if the user's sub
 * appears explicitly in ``permittedUsers``.
 *
 * @param dataset - The dataset record.
 * @param userSub - The Auth0 'sub' claim of the user to check.
 * @returns True if the user has access, false otherwise.
 */
export function isSharedWithUser(dataset: Dataset, userSub: string): boolean {
  if (dataset.owner === userSub) return true
  const permitted = dataset.permittedUsers ?? []
  return permitted.includes(PERMITTED_USERS_ALL_SENTINEL) || permitted.includes(userSub)
}

// ---------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------

/**
 * Explicit display labels for domain values whose pretty form is not just a
 * capitalisation of the raw value. Any value not listed here falls back to
 * capitalising its first letter (see prettifyDomainValue).
 */
const DOMAIN_VALUE_LABELS: Record<string, string> = {
  PERM_TO_RELEASE: "Permission to Release",
}

/**
 * Convert a raw domain value into a human-readable label for display.
 *
 * Uses an explicit override from DOMAIN_VALUE_LABELS when present; otherwise
 * lower-cases the value and capitalises its first letter (e.g. "annual" →
 * "Annual", "UNKNOWN" → "Unknown").
 *
 * @param value - The raw domain value (as stored / sent to the API).
 * @returns The display label.
 */
export function prettifyDomainValue(value: string): string {
  const override = DOMAIN_VALUE_LABELS[value]
  if (override !== undefined) {
    return override
  }
  if (value.length === 0) {
    return value
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

// ---------------------------------------------------------------------------
// Create dataset
// ---------------------------------------------------------------------------

/**
 * Payload for creating a new dataset. Mirrors the intended POST /api/datasets
 * request body. The dataset's identity (datasetId) is assigned server-side, so
 * it is not part of this request.
 */
export interface NewDatasetRequest {
  /** Human-readable dataset title (required). Shared with the biblio record. */
  title: string
  /** Free-text description of the dataset (optional). */
  description?: string
  /** Dataset topics, a subset of DatasetMetadataDomains.dataTopic (required, at least one). */
  dataTopic: string[]
  /** File keys for S3 upload — typically the file names (required, at least one). */
  fileKeys: string[]
  /** Users granted read access beyond the owner (optional). */
  permittedUsers?: string[]
  /** Temporal coverage, e.g. "2000" or "2000-2020" (optional). */
  temporalDimension?: string
  /** Temporal resolutions, a subset of DatasetMetadataDomains.temporalResolution (optional). */
  temporalResolution?: string[]
  /** Countries (and/or the "Global" sentinel) the dataset covers (optional). */
  countries?: string[]
  /** Spatial aggregation levels, a subset of DatasetMetadataDomains.spatialAggregationLevel (optional). */
  spatialAggregationLevel?: string[]
  /** Unit(s) of analysis (optional). May contain seed values from the domain
   *  list and/or a free-form value entered via the "Other" checkbox. */
  unitOfAnalysis?: string[]

  // --- Sharing agreement fields ---

  /** File keys for the sharing agreement document(s) uploaded to
   *  s3://{bucket}/{datasetId}/sharing_agreement/{filename} (optional). */
  sharingAgreementFileKeys?: string[]
  /** Free-form text describing usage permissions for the dataset (optional). */
  sharingAgreementText?: string

  // --- Source (biblio) fields ---
  // These describe the source/provenance of the data and are destined for the
  // biblio record rather than the datasets record.

  /** Release status, one of DatasetMetadataDomains.publishable (required). */
  publishable: string
  /** Contact names who sent/provided the dataset (optional). */
  biblioContacts?: string[]
  /** ISO date of the communication, e.g. "2024-10-15" (optional). */
  biblioCommunicationDate?: string
}

/**
 * Response from step 1 (upload-urls). Returns the server-generated datasetId
 * and presigned URLs for uploading files.
 */
interface UploadUrlsResponse {
  /** The server-assigned dataset identifier (used in step 2). */
  datasetId: string
  /** Presigned S3 PUT URLs keyed by file key. */
  uploadUrls: Record<string, string>
  /** Presigned S3 PUT URLs for sharing agreement files (if any). */
  sharingAgreementUploadUrls?: Record<string, string>
}

/**
 * Response from step 2 (confirm/create). Returns after the DynamoDB record is
 * written.
 */
export interface CreateDatasetResponse {
  /** The server-assigned dataset identifier. */
  datasetId: string
  /** Human-readable status message. */
  message: string
}

/**
 * Upload a single file to S3 via a presigned PUT URL.
 *
 * @param url - The presigned S3 PUT URL.
 * @param file - The File object to upload.
 * @throws Error if the upload fails (non-OK HTTP status).
 */
async function uploadFileToPresignedUrl(url: string, file: File): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    body: file,
    // Don't set Content-Type — the presigned URL wasn't signed with one,
    // and S3 will auto-detect it from the file content.
  })

  if (!response.ok) {
    throw new Error(
      `Failed to upload '${file.name}': HTTP ${response.status}`
    )
  }
}

/**
 * Create a new dataset using a multi-step flow with biblio integration:
 *
 *   Step 1: POST /api/datasets/upload-urls
 *     Validates metadata, generates a datasetId, returns presigned PUT URLs.
 *
 *   Step 2: Upload all dataset files to S3 via the presigned URLs.
 *     Also uploads any sharing agreement files to S3.
 *
 *   Step 3: POST /api/biblio/personal-communications (if biblio fields provided)
 *     Creates a biblio source record. If an email file is attached, it's
 *     uploaded to S3 first via the biblio presigned URL flow.
 *
 *   Step 4: POST /api/datasets
 *     Sends metadata + datasetId + biblioId. The Lambda verifies files exist
 *     in S3, then writes the DynamoDB record.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param request - The dataset creation metadata payload.
 * @param files - The File objects to upload, matching request.fileKeys by name.
 * @param emailFile - Optional email file to attach to the biblio record.
 * @param sharingAgreementFiles - Optional sharing agreement file(s) to upload.
 * @returns A promise resolving to the create response (including datasetId).
 * @throws Error if any step fails (validation, upload, or confirmation).
 */
export async function createDataset(
  accessToken: string,
  request: NewDatasetRequest,
  files: File[],
  emailFile?: File,
  sharingAgreementFiles?: File[]
): Promise<CreateDatasetResponse> {
  const baseUrl = await loadApiBaseUrl()

  // Step 1: Get presigned upload URLs (no DynamoDB write yet).
  const urlsResponse = await fetch(`${baseUrl}/upload-urls`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  })

  if (!urlsResponse.ok) {
    const errorBody = await urlsResponse.json().catch(() => null)
    const message = errorBody?.message ?? errorBody?.error ?? `HTTP ${urlsResponse.status}`
    throw new Error(`Failed to get upload URLs: ${message}`)
  }

  const { datasetId, uploadUrls, sharingAgreementUploadUrls }: UploadUrlsResponse = await urlsResponse.json()

  // Step 2: Upload each dataset file to its presigned URL.
  const uploadPromises: Promise<void>[] = files.map((file) => {
    const presignedUrl = uploadUrls[file.name]
    if (!presignedUrl) {
      return Promise.reject(
        new Error(`No presigned URL returned for file '${file.name}'`)
      )
    }
    return uploadFileToPresignedUrl(presignedUrl, file)
  })

  // Upload sharing agreement files (if any).
  if (sharingAgreementFiles && sharingAgreementUploadUrls) {
    for (const saFile of sharingAgreementFiles) {
      const presignedUrl = sharingAgreementUploadUrls[saFile.name]
      if (!presignedUrl) {
        uploadPromises.push(
          Promise.reject(
            new Error(`No presigned URL returned for sharing agreement file '${saFile.name}'`)
          )
        )
      } else {
        uploadPromises.push(uploadFileToPresignedUrl(presignedUrl, saFile))
      }
    }
  }

  await Promise.all(uploadPromises)

  // Step 3: Create biblio personal communication record.
  // Import dynamically to avoid circular dependencies.
  const { addPersonalCommunication } = await import("./biblioService")

  // Build the biblio contacts list as {name} objects.
  // Filter out empty names.
  const biblioContacts: { name: string }[] = (request.biblioContacts ?? [])
    .map((name) => ({ name: name.trim() }))
    .filter((contact) => contact.name.length > 0)

  // Build the biblio title: "Personal communication from {firstContactName}" or just "Personal communication"
  const firstContactName = biblioContacts[0]?.name ?? ""
  const biblioTitle = firstContactName
    ? `Personal communication from ${firstContactName}`
    : "Personal communication"

  const biblioResponse = await addPersonalCommunication(
    accessToken,
    {
      title: biblioTitle,
      contacts: biblioContacts.length > 0 ? biblioContacts : undefined,
      communicationDate: request.biblioCommunicationDate,
      usagePermission: request.publishable,
      datasetId: datasetId,
    },
    emailFile ? [emailFile] : undefined
  )
  const biblioId: number = biblioResponse.sourceId

  // Step 4: Confirm dataset — Lambda verifies files in S3, then writes DynamoDB.
  const confirmResponse = await fetch(baseUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ ...request, datasetId, biblioId }),
  })

  if (!confirmResponse.ok) {
    const errorBody = await confirmResponse.json().catch(() => null)
    const message = errorBody?.message ?? errorBody?.error ?? `HTTP ${confirmResponse.status}`
    throw new Error(`Failed to confirm dataset: ${message}`)
  }

  const result: CreateDatasetResponse = await confirmResponse.json()
  return result
}

// ---------------------------------------------------------------------------
// Update dataset
// ---------------------------------------------------------------------------

/**
 * Payload for updating an existing dataset's metadata.
 *
 * All fields are optional — only provided fields will be updated. Fields that
 * cannot be updated (files, owner, publishable) are not included in this type.
 */
export interface UpdateDatasetRequest {
  /** Human-readable dataset title. */
  title?: string
  /** Free-text description of the dataset. */
  description?: string
  /** Dataset topics (at least one required if provided). */
  dataTopic?: string[]
  /** Users granted read access beyond the owner. */
  permittedUsers?: string[]
  /** Temporal coverage, e.g. "2000" or "2000-2020". */
  temporalDimension?: string
  /** Temporal resolutions, a subset of DatasetMetadataDomains.temporalResolution. */
  temporalResolution?: string[]
  /** Countries (ISO3 codes) the dataset covers. */
  countries?: string[]
  /** Spatial aggregation levels. */
  spatialAggregationLevel?: string[]
  /** Unit(s) of analysis (seed values and/or a free-form "Other" value). */
  unitOfAnalysis?: string[]
  /** Free-form text describing usage permissions (empty string clears). */
  sharingAgreementText?: string
}

/**
 * Response from PATCH /api/datasets/{datasetId}.
 */
export interface UpdateDatasetResponse {
  /** Human-readable status message. */
  message: string
  /** The dataset ID that was updated. */
  datasetId: string
  /** The updated dataset record. */
  dataset: Dataset
}

/**
 * Update the metadata of an existing dataset.
 *
 * Calls PATCH /api/datasets/{datasetId}. Only the dataset owner can update.
 * Only provided fields will be updated — omitted fields remain unchanged.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param datasetId - The ID of the dataset to update.
 * @param updates - The fields to update (only non-undefined fields are sent).
 * @returns A promise resolving to the update response with the updated dataset.
 * @throws Error if the request fails (403 if not owner, 404 if not found, etc.).
 */
export async function updateDataset(
  accessToken: string,
  datasetId: string,
  updates: UpdateDatasetRequest
): Promise<UpdateDatasetResponse> {
  const baseUrl = await loadApiBaseUrl()

  const response = await fetch(`${baseUrl}/${datasetId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(updates),
  })

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null)
    const message =
      errorBody?.message ?? errorBody?.error ?? `HTTP ${response.status}`
    throw new Error(`Failed to update dataset: ${message}`)
  }

  const result: UpdateDatasetResponse = await response.json()
  return result
}

// ---------------------------------------------------------------------------
// List datasets
// ---------------------------------------------------------------------------

/**
 * Response from GET /api/datasets — the list of all datasets visible to the
 * authenticated user.
 */
interface ListDatasetsResponse {
  /** Array of dataset records. */
  datasets: Dataset[]
}

/**
 * Fetch all datasets visible to the authenticated user.
 *
 * Calls GET /api/datasets. Returns all datasets the user owns or has been
 * granted access to via permittedUsers.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @returns A promise resolving to the array of dataset records.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getDatasets(accessToken: string): Promise<Dataset[]> {
  const baseUrl = await loadApiBaseUrl()

  const response = await fetch(baseUrl, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch datasets: HTTP ${response.status}`)
  }

  const body: ListDatasetsResponse = await response.json()
  return body.datasets
}

// ---------------------------------------------------------------------------
// Add note
// ---------------------------------------------------------------------------

/**
 * Request body for adding a note to a dataset.
 */
export interface AddNoteRequest {
  /** The note text content. */
  note: string
}

/**
 * Response from POST /api/datasets/{datasetId}/notes.
 */
export interface AddNoteResponse {
  /** Human-readable status message. */
  message: string
  /** The dataset ID the note was added to. */
  datasetId: string
  /** The note that was added. */
  note: DatasetNote
}

/**
 * Add a note to a dataset.
 *
 * Calls POST /api/datasets/{datasetId}/notes. User must be the dataset owner
 * or be listed in permittedUsers.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param datasetId - The ID of the dataset to add the note to.
 * @param note - The note text content.
 * @returns A promise resolving to the add note response.
 * @throws Error if the request fails (403 if not authorized, 404 if not found).
 */
export async function addNote(
  accessToken: string,
  datasetId: string,
  note: string
): Promise<AddNoteResponse> {
  const baseUrl = await loadApiBaseUrl()

  const response = await fetch(`${baseUrl}/${datasetId}/notes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ note }),
  })

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null)
    const message =
      errorBody?.message ?? errorBody?.error ?? `HTTP ${response.status}`
    throw new Error(`Failed to add note: ${message}`)
  }

  const result: AddNoteResponse = await response.json()
  return result
}

// ---------------------------------------------------------------------------
// Download file
// ---------------------------------------------------------------------------

/**
 * Response from GET /api/datasets/{datasetId}/download?file={filename}&version={version}.
 */
export interface DownloadUrlResponse {
  /** The dataset ID. */
  datasetId: string
  /** The version that was downloaded. */
  version: number
  /** The requested filename. */
  filename: string
  /** The presigned S3 download URL. */
  downloadUrl: string
  /** URL expiry time in seconds (typically 900 = 15 minutes). */
  expiresInSeconds: number
}

/**
 * Get a presigned download URL for a file in a dataset.
 *
 * Calls GET /api/datasets/{datasetId}/download?file={filename}&version={version}&type={fileType}.
 * User must be the dataset owner or be listed in permittedUsers.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param datasetId - The ID of the dataset containing the file.
 * @param filename - The file key (name) to download.
 * @param version - The dataset version number (required, integer >= 1).
 * @param fileType - The type of file: "dataset" (default) or "sharing_agreement".
 * @returns A promise resolving to the download URL response.
 * @throws Error if the request fails (403 if not authorized, 404 if not found).
 */
export async function getDownloadUrl(
  accessToken: string,
  datasetId: string,
  filename: string,
  version: number,
  fileType: "dataset" | "sharing_agreement" = "dataset"
): Promise<DownloadUrlResponse> {
  const baseUrl = await loadApiBaseUrl()

  // URL-encode the filename to handle special characters and spaces.
  const encodedFilename = encodeURIComponent(filename)
  const url = `${baseUrl}/${datasetId}/download?file=${encodedFilename}&version=${version}&type=${fileType}`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null)
    const message =
      errorBody?.message ?? errorBody?.error ?? `HTTP ${response.status}`
    throw new Error(`Failed to get download URL: ${message}`)
  }

  const result: DownloadUrlResponse = await response.json()
  return result
}

/**
 * Download a file from a dataset.
 *
 * Gets a presigned URL from the API and triggers a browser download by
 * creating a temporary anchor element.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param datasetId - The ID of the dataset containing the file.
 * @param filename - The file key (name) to download.
 * @param version - The dataset version number (required, integer >= 1).
 * @param fileType - The type of file: "dataset" (default) or "sharing_agreement".
 * @throws Error if the request fails.
 */
export async function downloadFile(
  accessToken: string,
  datasetId: string,
  filename: string,
  version: number,
  fileType: "dataset" | "sharing_agreement" = "dataset"
): Promise<void> {
  const { downloadUrl } = await getDownloadUrl(accessToken, datasetId, filename, version, fileType)

  // Create a temporary anchor element to trigger the download.
  // This approach ensures the file downloads rather than opening in the browser.
  const anchor = document.createElement("a")
  anchor.href = downloadUrl
  anchor.download = filename
  // For cross-origin URLs, the download attribute may be ignored by some browsers,
  // but the presigned URL will still trigger a download due to Content-Disposition.
  anchor.style.display = "none"
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

// ---------------------------------------------------------------------------
// New version
// ---------------------------------------------------------------------------

/**
 * Request payload for creating a new version of a dataset.
 */
export interface NewVersionRequest {
  /** Explanation of what was wrong with the previous version (required). */
  deprecationReason: string
  /** File keys being freshly uploaded in this version (can be empty). */
  newFileKeys: string[]
  /** Files to carry forward from previous versions into the new version (can be empty). */
  carriedForwardFiles: CarriedForwardFile[]
}

/**
 * A single file to carry forward from a previous version into the new version.
 */
export interface CarriedForwardFile {
  /** The file name/key to copy. */
  fileKey: string
  /** The version number to copy this file from. */
  fromVersion: number
}

/**
 * Response from step 1 (upload-urls) of the new version flow.
 */
interface NewVersionUploadUrlsResponse {
  /** The dataset ID. */
  datasetId: string
  /** The version number that will be created. */
  newVersion: number
  /** Presigned S3 PUT URLs keyed by new file key. */
  uploadUrls: Record<string, string>
}

/**
 * Response from step 2 (confirm) of the new version flow.
 */
export interface NewVersionConfirmResponse {
  /** Human-readable status message. */
  message: string
  /** The dataset ID. */
  datasetId: string
  /** The newly created version number. */
  newVersion: number
  /** All file keys in the new version. */
  fileKeys: string[]
}

/**
 * Create a new version of an existing dataset.
 *
 * Uses a multi-step flow:
 *   Step 1: POST /api/datasets/{datasetId}/versions/upload-urls
 *     Validates the request, checks ownership, returns presigned PUT URLs for
 *     any new files being uploaded.
 *
 *   Step 2: Upload new files to S3 via the presigned URLs.
 *
 *   Step 3: POST /api/datasets/{datasetId}/versions
 *     The Lambda copies carried-forward files in S3, verifies all files exist,
 *     then atomically updates DynamoDB (increments currentVersion, adds version
 *     entry, sets deprecationReason on old version).
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param datasetId - The ID of the dataset to create a new version for.
 * @param request - The new version metadata (deprecation reason, file lists).
 * @param newFiles - The File objects to upload, matching request.newFileKeys by name.
 * @returns A promise resolving to the confirm response (including new version number).
 * @throws Error if any step fails (validation, upload, or confirmation).
 */
export async function createNewVersion(
  accessToken: string,
  datasetId: string,
  request: NewVersionRequest,
  newFiles: File[]
): Promise<NewVersionConfirmResponse> {
  const baseUrl = await loadApiBaseUrl()

  // Step 1: Get presigned upload URLs for the new files.
  const urlsResponse = await fetch(`${baseUrl}/${datasetId}/versions/upload-urls`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  })

  if (!urlsResponse.ok) {
    const errorBody = await urlsResponse.json().catch(() => null)
    const message = errorBody?.message ?? errorBody?.details?.join("; ") ?? errorBody?.error ?? `HTTP ${urlsResponse.status}`
    throw new Error(`Failed to get upload URLs: ${message}`)
  }

  const { uploadUrls }: NewVersionUploadUrlsResponse = await urlsResponse.json()

  // Step 2: Upload each new file to its presigned URL.
  const uploadPromises: Promise<void>[] = newFiles.map((file) => {
    const presignedUrl = uploadUrls[file.name]
    if (!presignedUrl) {
      return Promise.reject(
        new Error(`No presigned URL returned for file '${file.name}'`)
      )
    }
    return uploadFileToPresignedUrl(presignedUrl, file)
  })

  await Promise.all(uploadPromises)

  // Step 3: Confirm — Lambda copies carried-forward files, verifies, commits.
  const confirmResponse = await fetch(`${baseUrl}/${datasetId}/versions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  })

  if (!confirmResponse.ok) {
    const errorBody = await confirmResponse.json().catch(() => null)
    const message = errorBody?.message ?? errorBody?.details?.join("; ") ?? errorBody?.error ?? `HTTP ${confirmResponse.status}`
    throw new Error(`Failed to create new version: ${message}`)
  }

  const result: NewVersionConfirmResponse = await confirmResponse.json()
  return result
}
